"use client";

import { useEffect } from "react";
import Icon from "@/components/ui/AppIcon";
import Link from "next/link";

interface ServiceCategory {
  id: string;
  icon: string;
  title: string;
  gradient: string;
  features: string[];
}

export default function ServiceCategories() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -5% 0px" }
    );

    document.querySelectorAll(".animate-on-scroll").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const categories: ServiceCategory[] = [
    {
      id: "category_film",
      icon: "FilmIcon",
      title: "Film Production",
      gradient: "from-purple-900/40 to-blue-900/40",
      features: [
        "Pre-production planning & scripting",
        "Professional cinematography & direction",
        "Location scouting & management",
        "Equipment rental & technical support",
        "Full crew coordination",
        "On-set production management",
      ],
    },
    {
      id: "category_events",
      icon: "SparklesIcon",
      title: "Event Management",
      gradient: "from-emerald-900/40 to-teal-900/40",
      features: [
        "Corporate event production",
        "Live concert & performance staging",
        "Award ceremony coordination",
        "Festival planning & execution",
        "Technical setup & AV management",
        "On-site event coordination",
      ],
    },
    {
      id: "category_post",
      icon: "VideoCameraIcon",
      title: "Post-Production",
      gradient: "from-orange-900/40 to-red-900/40",
      features: [
        "Professional video editing",
        "Color grading & correction",
        "Sound design & mixing",
        "Visual effects & CGI",
        "Motion graphics & animation",
        "Final mastering & delivery",
      ],
    },
  ];

  return (
    <section id="production" className="py-24 md:py-32 px-6 md:px-12 relative">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 animate-on-scroll [animation:animationIn_0.8s_ease-out_0.2s_both]">
          <h2 className="text-3xl md:text-4xl font-display font-light text-white mb-4">
            Our Service Categories
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-light">
            Specialized expertise across every stage of entertainment production.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {categories.map((category, index) => (
            <div
              key={category.id}
              className={`glass-card glass-card-hover p-8 rounded-3xl transition-all duration-300 hover:-translate-y-2 animate-on-scroll [animation:animationIn_0.8s_ease-out_${
                0.3 + index * 0.1
              }s_both]`}
            >
              <div
                className={`w-16 h-16 rounded-full bg-gradient-to-br ${category.gradient} border border-white/5 flex items-center justify-center mb-6 text-white`}
              >
                <Icon name={category.icon as any} size={32} />
              </div>
              <h3 className="text-2xl font-display font-medium text-white mb-6">
                {category.title}
              </h3>
              <ul className="space-y-3 mb-8">
                {category.features.map((feature, idx) => (
                  <li
                    key={`${category.id}_feature_${idx}`}
                    className="flex items-start gap-3 text-sm text-muted-foreground"
                  >
                    <Icon
                      name="CheckCircleIcon"
                      size={20}
                      className="text-primary flex-shrink-0 mt-0.5"
                    />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:gap-3 transition-all"
              >
                Request Quote
                <Icon name="ArrowRightIcon" size={16} />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}